<template>
    <div v-if="visible" class="modal-overlay" @click="handleClose">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h2 class="modal-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    <span>{{ modalTitle }}</span>
                </h2>
                <button class="close-btn" @click="handleClose">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </button>
            </div>

            <div class="modal-body">
                <!-- 内容类型选择 -->
                <div class="content-type-selector" v-if="!editData">
                    <h3 class="section-title">选择内容类型</h3>
                    <div class="type-options">
                        <div :class="['type-option', {
                            active: contentData.type === 'message',
                            disabled: !userMassSetting.message
                        }]" @click="selectContentType('message')" :title="getTypeOptionTooltip('message')">
                            <div class="type-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                                </svg>
                            </div>
                            <div class="type-info">
                                <div class="type-name">消息</div>
                                <div class="type-desc">文本 + 图片（可选，最多6张）</div>
                            </div>
                        </div>
                        <div :class="['type-option', {
                            active: contentData.type === 'email',
                            disabled: !userMassSetting.mail
                        }]" @click="selectContentType('email')" :title="getTypeOptionTooltip('email')">
                            <div class="type-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                                </svg>
                            </div>
                            <div class="type-info">
                                <div class="type-name">邮件</div>
                                <div class="type-desc">内容 + 图片或视频（可选，图片最多5张，视频最多3个）</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 编辑模式下的内容类型显示 -->
                <div class="content-type-display" v-if="editData">
                    <h3 class="section-title">内容类型</h3>
                    <div class="type-display">
                        <div class="type-icon">
                            <svg v-if="contentData.type === 'message'" width="20" height="20" viewBox="0 0 24 24"
                                fill="currentColor">
                                <path
                                    d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                            </svg>
                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                        </div>
                        <div class="type-info">
                            <div class="type-name">{{ contentData.type === 'message' ? '消息' : '邮件' }}</div>
                            <div class="type-desc">
                                {{ contentData.type === 'message' ? '文本 + 图片（可选，最多6张）' :
                                    '内容 + 图片或视频（可选，图片最多5张，视频最多3个）' }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 语气词和昵称输入区域 -->
                <div class="tone-nickname-container">
                    <div class="input-row">
                        <div class="input-group">
                            <label class="input-label">语气词</label>
                            <input 
                                v-model="contentData.tone" 
                                type="text" 
                                class="content-input" 
                                placeholder="如：hello、hi、dear等"
                                maxlength="20">
                            <div class="input-hint">如：hello、hi、dear等</div>
                        </div>
                        <div class="input-group">
                            <label class="input-label">昵称</label>
                            <div class="nickname-display">
                                不可设置(占位符为##$$##)
                            </div>
                            <div class="input-hint">代表接收群发用户的昵称（自动生成发送用户的昵称）</div>
                        </div>
                    </div>
                </div>

                <!-- 文本输入区域 -->
                <div class="text-input-container">
                    <div class="text-header">
                        <h3 class="section-title">{{ textAreaTitle }}</h3>
                        <emoji-picker :onInsertEmoji="handleInsertEmoji" />
                    </div>
                    <textarea 
                        ref="contentTextarea"
                        v-model="contentData.text" 
                        class="content-textarea" 
                        :placeholder="textAreaPlaceholder"
                        :maxlength="contentData.type === 'email' ? 3500 : 300"
                        @blur="saveCaretPosition">
                    </textarea>
                    <div class="char-count">
                        <span
                            :class="{ 'char-warning': contentData.type === 'email' && contentData.text.length < 150 }">
                            {{ contentData.text.length }}/{{ contentData.type === 'email' ? 3500 : 300 }}
                        </span>
                        <span v-if="contentData.type === 'email' && contentData.text.length < 150"
                            class="min-char-hint">
                            (最少需要150个字符)
                        </span>
                    </div>
                </div>
                
                <!-- 提示信息 -->
                <div class="greeting-hint">
                    <i class="el-icon-info"></i>
                    <div class="hint-content">
                        <div class="hint-line">
                            <span class="hint-label">示例说明：</span>
                        </div>
                        <div class="hint-line">
                            若语气词填写 <strong>"Hi"</strong>，消息内容填写 <strong>"123"</strong>，发给 <strong>Jack</strong>的群发消息将显示为：<strong class="example-text">Hi Jack, 123</strong>
                        </div>
                        <div class="hint-line">
                            若语气词不填写，消息内容填写 <strong>"123"</strong>，发给 <strong>Jack</strong>的群发消息将显示为：<strong class="example-text">123</strong>
                        </div>
                    </div>
                </div>
                
                <!-- 媒体上传区域 -->
                <div class="media-upload-section">
                    <div class="media-header">
                        <h3 class="section-title">添加媒体</h3>
                        <div class="media-counts">
                            <span v-if="contentData.type === 'message'" class="count-item">
                                图片: {{ contentData.images.length }}/6
                            </span>
                            <span v-if="contentData.type === 'email'" class="count-item">
                                图片: {{ contentData.images.length }}/5
                            </span>
                            <span v-if="contentData.type === 'email'" class="count-item">
                                视频: {{ contentData.videos.length }}/3
                            </span>
                        </div>
                    </div>



                    <!-- 媒体容器 -->
                    <div class="media-container">
                        <!-- 上传区域 -->
                        <div class="upload-area" @click="openAttachmentModal"
                            :class="{ disabled: !canAddImage && !canAddVideo }">
                            <div class="upload-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                </svg>
                            </div>
                            <div class="upload-text">{{ uploadButtonText }}</div>
                            <div class="upload-hint">{{ uploadHintText }}</div>
                        </div>

                        <!-- 已选择的图片预览 -->
                        <div v-for="(image, index) in contentData.images" :key="`img-${index}`" class="media-preview">
                            <img :src="getMediaUrl(image)" :alt="`图片 ${index + 1}`" />
                            <div class="media-number">{{ index + 1 }}</div>
                            <button class="remove-media" @click="removeImage(index)" title="删除图片">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </svg>
                            </button>
                        </div>

                        <!-- 已选择的视频预览 -->
                        <div v-for="(video, index) in contentData.videos" :key="`vid-${index}`" class="media-preview">
                            <video :src="getMediaUrl(video)" controls></video>
                            <div class="media-number">{{ index + 1 }}</div>
                            <button class="remove-media" @click="removeVideo(index)" title="删除视频">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- 媒体要求提示 -->
                    <div v-if="showMediaWarning" class="media-warning">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                        </svg>
                        <span>{{ mediaWarningText }}</span>
                    </div>
                </div>

                <!-- 按钮区域 -->
                <div class="modal-actions">
                    <button class="btn btn-secondary" @click="handleClose">
                        <span>取消</span>
                    </button>
                    <button class="btn btn-primary" @click="handleSubmit" :disabled="!canSubmit">
                        <span>{{ editData ? '保存修改' : '创建内容' }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Attachment组件 -->
        <attachment ref="attachmentDom" :chatUserInfo="attachmentUserInfo" :approvedOnly="true"
            @send-sonMediaMessage="handleAttachmentSelection" @visible-change="handleAttachmentVisibleChange"
            :currentMessageType="currentMessageType" :hideAudio="true" :enableContinuousLoad="true" />
    </div>
</template>

<script>
import attachment from '@/views/talkytimes/views/masSending/components/attachment.vue'
import EmojiPicker from '@/views/talkytimes/views/message/components/emoji-picker.vue'
import { addTaskTemplate, editTaskTemplate } from '@/api/talkytimes/masSending'

export default {
    name: 'CreateContentModal',
    components: {
        attachment,
        EmojiPicker
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        userMassSetting: {
            type: Object,
            default: () => ({
                message: false,
                mail: false
            })
        },
        selectedUser: {
            type: Object,
            default: () => ({})
        },
        editData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            contentData: {
                type: 'message', // 默认选择消息类型
                tone: '', // 语气词
                nickname: '', // 昵称（自动获取）
                text: '',
                images: [],
                videos: []
            },
            attachmentUserInfo: {
                userId: this.selectedUser?.id || this.$store.state.user.userId || 'default',
                platformUserId: this.selectedUser?.pdUserAccount || this.$store.state.user.platformUserId || 'default',
                userDetail: {
                    name: this.selectedUser?.name || this.$store.state.user.name || '用户',
                    avatar_large: this.selectedUser?.personalAvatar_xl || this.$store.state.user.avatar || null
                }
            },
            attachmentVisible: false,
            currentMessageType: 'chat',
            caretPosition: 0, // 文本框光标位置
            isSubmitting: false // 提交中状态
        }
    },
    computed: {
        modalTitle() {
            const action = this.editData ? '编辑' : '创建';
            return this.contentData.type === 'message' ? `${action}消息` : `${action}邮件`
        },
        textAreaTitle() {
            return this.contentData.type === 'message' ? '消息内容' : '邮件内容'
        },
        textAreaPlaceholder() {
            return this.contentData.type === 'message'
                ? '在这里写下您的消息内容...'
                : '在这里写下您的邮件内容...'
        },
        uploadButtonText() {
            if (this.contentData.type === 'message') {
                return this.contentData.images.length >= 6 ? '已达到上限' : '添加图片'
            } else {
                if (this.contentData.images.length >= 5 && this.contentData.videos.length >= 3) {
                    return '已达到上限'
                } else if (this.contentData.images.length >= 5) {
                    return '添加视频'
                } else if (this.contentData.videos.length >= 3) {
                    return '添加图片'
                } else {
                    return '添加媒体'
                }
            }
        },
        uploadHintText() {
            if (this.contentData.type === 'message') {
                return '支持 JPG、PNG 格式，可选，最多6张'
            } else {
                return '支持 JPG、PNG、MP4、MOV 格式，可选，图片最多5张，视频最多3个'
            }
        },
        isUploadDisabled() {
            if (this.contentData.type === 'message') {
                return this.contentData.images.length >= 6
            } else {
                // 邮件类型：图片达到5张且视频达到3个时禁用上传
                return this.contentData.images.length >= 5 && this.contentData.videos.length >= 3
            }
        },
        // 检查是否可以添加图片
        canAddImage() {
            if (this.contentData.type === 'message') {
                return this.contentData.images.length < 6
            } else {
                return this.contentData.images.length < 5
            }
        },
        // 检查是否可以添加视频
        canAddVideo() {
            if (this.contentData.type === 'message') {
                return false // 消息类型不支持视频
            } else {
                return this.contentData.videos.length < 3
            }
        },
        showMediaWarning() {
            // 图片和视频都是非必选的，不显示警告
            return false
        },
        mediaWarningText() {
            // 图片和视频都是非必选的，不需要警告文本
            return ''
        },
        canSubmit() {
            const toneValue = this.contentData.tone.trim();
            const textValue = this.contentData.text.trim();

            if (!toneValue) return false;

            const finalContent = this.buildFinalContent(toneValue, textValue);

            if (this.contentData.type === 'email' && finalContent.length < 150) return false;

            return true;
        },
        // Attachment组件参数计算属性
        attachmentPhotoOnly() {
            // 消息类型只允许图片
            return this.contentData.type === 'message'
        },
        attachmentVideoOnly() {
            // 邮件类型允许图片和视频
            return false
        },
        attachmentMessageType() {
            // 根据内容类型返回不同的消息类型
            if (this.contentData.type === 'message') {
                return 'newsFeed'
            } else if (this.contentData.type === 'masSending') {
                return 'masSending'
            } else {
                return 'email'
            }
        }
    },
    mounted() {
        // 如果初始就有编辑数据，则初始化
        if (this.editData) {
            this.initEditData();
        }
    },
    methods: {
        handleClose() {
            this.$emit('close');
            this.resetForm();
        },
        // 设置编辑数据的方法
        setEditData(editData) {
            if (editData) {
                console.log('setEditData - editData:', editData);

                // 一次性设置所有数据，避免多次触发响应式更新
                const newContentData = {
                    type: editData.type || ((editData.jobType === 1 || editData.jobType === '1') ? 'message' : 'email'),
                    tone: editData.tone || '',
                    nickname: editData.nickname || '',
                    text: editData.content || editData.messageContent || '',
                    images: [],
                    videos: []
                };

                // 设置图片
                if (editData.imageList && editData.imageList.length > 0) {
                    newContentData.images = editData.imageList.map((url, index) => ({
                        url: url,
                        name: '图片',
                        type: 'image'
                    }));
                } else if (editData.imagesUrl) {
                    const imageUrls = editData.imagesUrl.split(',').filter(url => url.trim());
                    const imageIds = editData.imagesId ? editData.imagesId.split(',').filter(id => id.trim()) : [];
                    newContentData.images = imageUrls.map((url, index) => ({
                        url: url.trim(),
                        idPhoto: imageIds[index] || '',
                        name: '图片',
                        type: 'image'
                    }));
                }

                // 设置视频
                if (editData.videoList && editData.videoList.length > 0) {
                    newContentData.videos = editData.videoList.map((url, index) => ({
                        url: url,
                        name: '视频',
                        type: 'video'
                    }));
                } else if (editData.videoUrl) {
                    const videoUrls = editData.videoUrl.split(',').filter(url => url.trim());
                    const videoIds = editData.videoId ? editData.videoId.split(',').filter(id => id.trim()) : [];
                    newContentData.videos = videoUrls.map((url, index) => ({
                        url: url.trim(),
                        idVideo: videoIds[index] || '',
                        name: '视频',
                        type: 'video'
                    }));
                }

                const { tone, text } = this.extractToneAndText(newContentData.text, newContentData.tone);
                newContentData.tone = tone;
                newContentData.text = text;

                // 一次性替换整个 contentData 对象
                this.contentData = newContentData;
                console.log('setEditData - contentData after set:', this.contentData);
            }
        },

        // 初始化编辑数据（保留用于兼容性）
        initEditData() {
            if (this.editData) {
                this.setEditData(this.editData);
            }
        },
        selectContentType(type) {
            // 编辑模式下不允许切换内容类型
            if (this.editData) {
                this.$emit('error', '编辑模式下不允许切换内容类型');
                return;
            }

            // 检查是否禁用
            if (type === 'message' && !this.userMassSetting.message) {
                this.$emit('error', '请先设置消息模板');
                return;
            }
            if (type === 'email' && !this.userMassSetting.mail) {
                this.$emit('error', '请先设置邮件模板');
                return;
            }

            this.contentData.type = type;
            this.contentData.images = [];
            this.contentData.videos = [];
        },
        openAttachmentModal() {
            if (this.contentData.type === 'message') {
                if (!this.canAddImage) {
                    this.$emit('error', '最多只能上传6张图片');
                    return;
                }
            } else {
                // 邮件类型：检查是否还能添加任何媒体
                if (!this.canAddImage && !this.canAddVideo) {
                    this.$emit('error', '图片和视频都已达到上限（图片最多5张，视频最多3个）');
                    return;
                }
            }
            this.$refs.attachmentDom.init();
        },
        handleAttachmentSelection(data) {
            console.log('handleAttachmentSelection:::::::', data);
            const selectedItems = data.dataMsgList || [];
            const mediaType = data.bTypeVal; // 'image' 或 'video'
            console.log('selectedItems:::::::', selectedItems);
            console.log('mediaType:::::::', mediaType);

            // 根据当前内容类型处理选择
            if (this.contentData.type === 'message') {
                // 消息类型只处理图片
                if (mediaType === 'image') {
                    this.handleImageSelection(selectedItems, 6);
                } else {
                    this.$emit('error', '消息类型只支持图片');
                }
            } else {
                // 邮件类型根据媒体类型分别处理
                if (mediaType === 'image') {
                    if (this.canAddImage) {
                        this.handleImageSelection(selectedItems, 5);
                    } else {
                        this.$emit('error', '图片已达到上限（最多5张）');
                    }
                } else if (mediaType === 'video') {
                    if (this.canAddVideo) {
                        this.handleVideoSelection(selectedItems, 3);
                    } else {
                        this.$emit('error', '视频已达到上限（最多3个）');
                    }
                }
            }
        },

        handleImageSelection(selectedItems, maxCount) {
            console.log('handleImageSelection called with:', selectedItems, 'maxCount:', maxCount);
            console.log('current images length:', this.contentData.images.length);

            const remainingSlots = maxCount - this.contentData.images.length;

            if (selectedItems.length > remainingSlots) {
                this.$emit('error', `最多只能再添加${remainingSlots}张图片`);
                selectedItems.splice(remainingSlots);
            }

            this.contentData.images = [...this.contentData.images, ...selectedItems];
            console.log('updated images:', this.contentData.images);
        },

        handleVideoSelection(selectedItems, maxCount) {
            console.log('handleVideoSelection called with:', selectedItems, 'maxCount:', maxCount);
            console.log('current videos length:', this.contentData.videos.length);

            const remainingSlots = maxCount - this.contentData.videos.length;

            if (selectedItems.length > remainingSlots) {
                this.$emit('error', `最多只能再添加${remainingSlots}个视频`);
                selectedItems.splice(remainingSlots);
            }

            this.contentData.videos = [...this.contentData.videos, ...selectedItems];
            console.log('updated videos:', this.contentData.videos);
        },
        handleAttachmentVisibleChange(visible) {
            this.attachmentVisible = visible;
        },
        getMediaUrl(file) {
            if (file instanceof File) {
                return URL.createObjectURL(file);
            }
            // 对于视频，优先使用urlHd，然后是urlSd
            if (file.urlHd || file.urlSd) {
                return file.urlHd || file.urlSd;
            }
            // 对于图片，使用原有的字段
            return file.url || file.originalUrl || file;
        },
        removeImage(index) {
            this.contentData.images.splice(index, 1);
        },
        removeVideo(index) {
            this.contentData.videos.splice(index, 1);
        },
        getTypeOptionTooltip(type) {
            if (type === 'message') {
                return this.userMassSetting.message ? '点击选择消息类型' : '请先设置消息模板';
            } else {
                return this.userMassSetting.mail ? '点击选择邮件类型' : '请先设置邮件模板';
            }
        },
        getDefaultContentType() {
            // 优先选择有模板的内容类型
            if (this.userMassSetting.message) {
                return 'message';
            } else if (this.userMassSetting.mail) {
                return 'email';
            } else {
                // 如果都没有模板，默认选择消息
                return 'message';
            }
        },
        handleSubmit() {
            const tone = this.contentData.tone.trim();
            const text = this.contentData.text.trim();

            if (!tone) {
                this.$emit('error', '请先填写语气词');
                return;
            }

            const finalContent = this.buildFinalContent(tone, text);

            if (this.contentData.type === 'email' && finalContent.length < 150) {
                this.$emit('error', `邮件内容至少需要150个字符，当前只有${finalContent.length}个字符`);
                return;
            }

            if (!this.selectedUser || !this.selectedUser.id) {
                this.$emit('error', '请先选择一个用户');
                return;
            }

            const imagesId = this.contentData.images.length > 0
                ? this.contentData.images.map(img => img.idPhoto || img.id).join(',')
                : '';
            const imagesUrl = this.contentData.images.length > 0
                ? this.contentData.images.map(img => img.url || img.originalUrl || '').join(',')
                : '';
            const videoId = this.contentData.videos.length > 0
                ? this.contentData.videos.map(vid => vid.idVideo || vid.id).join(',')
                : '';
            const videoUrl = this.contentData.videos.length > 0
                ? this.contentData.videos.map(vid => vid.urlHd || vid.urlSd || '').join(',')
                : '';

            const taskData = {
                idUser: this.selectedUser.id,
                messageContent: finalContent,
                imagesUrl: imagesUrl,
                imagesId: imagesId,
                videoUrl: videoUrl,
                videoId: videoId,
                jobType: this.contentData.type === 'message' ? '1' : '0',
                jobId: this.contentData.type === 'message'
                    ? this.userMassSetting.messageJobId
                    : this.userMassSetting.mailJobId,
                pdUserAccount: this.selectedUser.pdUserAccount,
                relevancyToken: this.selectedUser.pdToken
            };

            if (this.contentData.type === 'message' && this.selectedUser.userMassSetting?.messageJobId) {
                taskData.jobId = this.selectedUser.userMassSetting.messageJobId;
            } else if (this.contentData.type === 'email' && this.selectedUser.userMassSetting?.mailJobId) {
                taskData.jobId = this.selectedUser.userMassSetting.mailJobId;
            }

            if (this.editData) {
                taskData.id = this.editData.id;
            }

            const confirmTitle = this.editData ? '确认修改内容' : '确认创建内容';
            const confirmButtonText = this.editData ? '确认修改' : '确认创建';
            const previewHtml = this.buildConfirmHtml({
                tone,
                text,
                finalContent,
                type: this.contentData.type
            });

            this.$confirm(previewHtml, confirmTitle, {
                confirmButtonText,
                cancelButtonText: '返回修改',
                dangerouslyUseHTMLString: true,
                customClass: 'create-content-confirm'
            })
                .then(() => {
                    this.submitTask(taskData);
                })
                .catch(() => {
                    // 用户取消确认，无需处理
                });
        },
        extractToneAndText(rawContent = '', rawTone = '') {
            const placeholder = '##$$##';
            let tone = (rawTone || '').trim();
            let content = rawContent || '';

            if (!content) {
                return { tone, text: '' };
            }

            const placeholderIndex = content.indexOf(placeholder);

            if (placeholderIndex !== -1) {
                if (!tone) {
                    const before = content.slice(0, placeholderIndex).trim();
                    tone = before.replace(/[，,\s]+$/g, '').trim();
                }

                let after = content.slice(placeholderIndex + placeholder.length);
                if (after.startsWith(',')) {
                    after = after.slice(1);
                }
                after = after.replace(/^[，,\s]+/, '');
                content = after.trim();
            } else {
                content = content.trim();
            }

            return {
                tone,
                text: content
            };
        },
        buildFinalContent(tone, text) {
            if (tone && text) {
                return `${tone} ##$$##, ${text}`;
            } else if (tone) {
                return `${tone} ##$$##`;
            }
            return text;
        },
        buildConfirmHtml({ tone, text, finalContent, type }) {
            const typeLabel = type === 'message' ? '消息' : '邮件';
            const contentLabel = type === 'message' ? '消息内容' : '邮件内容';
            const escapedTone = tone ? this.escapeHtml(tone) : '（未填写）';
            const escapedText = text ? this.escapeHtml(text) : '（未填写）';
            const attachments = [];
            if (this.contentData.images.length > 0) {
                attachments.push(`图片 ${this.contentData.images.length} 张`);
            }
            if (this.contentData.videos.length > 0) {
                attachments.push(`视频 ${this.contentData.videos.length} 个`);
            }
            const previewMessage = tone
                ? `${this.escapeHtml(tone)} 【用户昵称】${text ? `, ${this.escapeHtml(text)}` : ''}`
                : (text ? this.escapeHtml(text) : '（未填写）');

            return `
                <div class="create-content-confirm-body" style="max-height: 500px; overflow: scroll;">
                    <p><strong>内容类型：</strong>${typeLabel}</p>
                    <p><strong>语气词：</strong>${escapedTone}</p>
                    <p><strong>${contentLabel}：</strong>${escapedText}</p>
                    <p><strong>示例预览：</strong>${previewMessage}</p>
                    <p><strong>最终发送内容：</strong>${this.escapeHtml(finalContent)}</p>
                    <p><strong>媒体附件：</strong>${attachments.length > 0 ? attachments.join('，') : '无'}</p>
                </div>
            `;
        },
        submitTask(taskData) {
            const apiCall = this.editData ? editTaskTemplate : addTaskTemplate;
            const actionText = this.editData ? '编辑' : '创建';

            // 显示loading动画
            this.isSubmitting = true;
            const loadingInstance = this.$loading({
                lock: true,
                text: this.editData ? '正在修改...' : '正在创建...',
                spinner: 'el-icon-loading',
                background: 'rgba(0, 0, 0, 0.7)'
            });

            apiCall(taskData)
                .then(res => {
                    loadingInstance.close();
                    this.isSubmitting = false;
                    this.$emit('success', {
                        type: this.contentData.type,
                        data: res
                    });
                    this.handleClose();
                })
                .catch(error => {
                    loadingInstance.close();
                    this.isSubmitting = false;
                    console.error(`${actionText}任务模板失败:`, error);
                    this.$emit('error', `${actionText}任务模板失败，请重试`);
                });
        },
        escapeHtml(str = '') {
            return String(str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
        resetForm() {
            // 重置表单为默认状态
            this.contentData = {
                type: 'message',
                tone: '',
                nickname: '',
                text: '',
                images: [],
                videos: []
            };
        },
        
        // 保存文本框光标位置
        saveCaretPosition() {
            const textarea = this.$refs.contentTextarea;
            if (textarea) {
                this.caretPosition = textarea.selectionStart;
            }
        },
        
        // 在文本框光标位置插入表情
        handleInsertEmoji(emoji) {
            const textarea = this.$refs.contentTextarea;
            if (!textarea) return;

            const text = this.contentData.text;
            const position = this.caretPosition || text.length;
            
            // 在光标位置插入表情
            const before = text.slice(0, position);
            const after = text.slice(position);
            this.contentData.text = before + emoji + after;
            
            // 更新光标位置
            this.$nextTick(() => {
                const newPosition = position + emoji.length;
                textarea.focus();
                textarea.setSelectionRange(newPosition, newPosition);
                this.caretPosition = newPosition;
            });
        }
    },
    watch: {
        // 监听模态框显示状态
        visible(newVal) {
            if (newVal && this.editData) {
                this.setEditData(this.editData);
            }
        },
        userMassSetting: {
            handler(newVal) {
                // 当用户设置状态变化时，设置默认内容类型（仅在非编辑模式下）
                if (!this.editData) {
                    this.contentData.type = this.getDefaultContentType();
                }
            },
            immediate: true
        },
        selectedUser: {
            handler(newVal) {
                // 当选中的用户变化时，更新attachmentUserInfo
                if (newVal) {
                    this.attachmentUserInfo = {
                        userId: newVal.id || this.$store.state.user.userId || 'default',
                        platformUserId: newVal.pdUserAccount || this.$store.state.user.platformUserId || 'default',
                        userDetail: {
                            name: newVal.name || this.$store.state.user.name || '用户',
                            avatar_large: newVal.personalAvatar_xl || this.$store.state.user.avatar || null
                        }
                    };
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        // 组件挂载完成
    }
}
</script>

<style scoped lang="scss">
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-content {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    width: 90%;
    max-width: 800px;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 32px;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 20px 20px 0 0;
}

.modal-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
}

.modal-title svg {
    width: 28px;
    height: 28px;
    color: #8b5cf6;
}

.close-btn {
    background: rgba(243, 244, 246, 0.8);
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #6b7280;
    padding: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.close-btn:hover {
    color: #1f2937;
    background: rgba(239, 68, 68, 0.1);
    transform: rotate(90deg);
}

.modal-body {
    padding: 16px 24px;
}

.content-type-selector {
    margin-bottom: 16px;
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.type-options {
    display: flex;
    gap: 12px;
}

.type-option {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 2px solid rgba(229, 231, 235, 0.8);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: rgba(249, 250, 251, 0.8);
}

.type-option:hover {
    border-color: #8b5cf6;
    background: rgba(240, 247, 255, 0.8);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
}

.type-option.active {
    border-color: #8b5cf6;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
}

.type-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 8px;
    color: #8b5cf6;
    transition: all 0.3s ease;
}

.type-option.active .type-icon {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.type-info {
    flex: 1;
}

.type-name {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 2px;
}

.type-desc {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.3;
}

.type-option.active .type-desc {
    color: rgba(255, 255, 255, 0.8);
}

.type-option.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(156, 163, 175, 0.1);
    border-color: rgba(156, 163, 175, 0.3);
}

.type-option.disabled:hover {
    background: rgba(156, 163, 175, 0.1);
    border-color: rgba(156, 163, 175, 0.3);
    transform: none;
    box-shadow: none;
}

.type-option.disabled .type-icon {
    color: #9ca3af;
    background: rgba(156, 163, 175, 0.1);
}

.type-option.disabled .type-name {
    color: #9ca3af;
}

.type-option.disabled .type-desc {
    color: #9ca3af;
}

/* 编辑模式下的内容类型显示样式 */
.content-type-display {
    margin-bottom: 24px;
}

.type-display {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: rgba(139, 92, 246, 0.05);
    border: 2px solid rgba(139, 92, 246, 0.2);
    border-radius: 12px;
    transition: all 0.3s ease;
}

.type-display .type-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 8px;
    color: #8b5cf6;
    flex-shrink: 0;
}

.tone-nickname-container {
    margin-bottom: 24px;

    .input-row {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
        width: 100%;
    }

    .input-group {
        flex: 1; // 平均分配宽度
        display: flex;
        flex-direction: column;

        .input-label {
            font-size: 14px;
            font-weight: 500;
            color: #333;
            margin-bottom: 8px;
        }

        .content-input {
            // width: 100%;
            padding: 12px 16px;
            border: 2px solid #e1e5e9;
            border-radius: 8px;
            font-size: 14px;
            transition: all 0.3s ease;
            background: #fff;

            &:focus {
                outline: none;
                border-color: #8b5cf6;
                box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
            }

            &::placeholder {
                color: #9ca3af;
            }
        }

        .input-hint {
            font-size: 12px;
            color: #6b7280;
            margin-top: 4px;
        }

        .nickname-display {
            // width: 100%;
            padding: 12px 16px;
            border: 2px solid #e1e5e9;
            border-radius: 8px;
            font-size: 14px;
            background: #f8f9fa;
            color: #6b7280;
            min-height: 20px;
            display: flex;
            align-items: center;
            transition: all 0.3s ease;

            &:empty::before {
                content: '未设置昵称';
                color: #9ca3af;
            }
        }
    }
}

.text-input-container {
    position: relative;
    margin-bottom: 16px;
}

.text-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.content-textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 2px solid rgba(209, 213, 219, 0.8);
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 0.8);
    transition: all 0.3s ease;
    line-height: 1.4;
}

.content-textarea:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
    background: white;
}

.content-textarea::placeholder {
    color: #9ca3af;
    font-weight: 400;
}

.char-count {
    position: absolute;
    bottom: 12px;
    right: 12px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    background: rgba(255, 255, 255, 0.9);
    padding: 4px 8px;
    border-radius: 6px;
}

.media-upload-section {
    margin-bottom: 16px;
}

.media-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.media-counts {
    display: flex;
    gap: 16px;
}

.count-item {
    font-size: 14px;
    font-weight: 500;
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
    padding: 4px 10px;
    border-radius: 8px;
}



.media-container {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-start;
}

.upload-area {
    width: 100px;
    height: 100px;
    border: 2px dashed rgba(209, 213, 219, 0.8);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: rgba(249, 250, 251, 0.8);
    flex-shrink: 0;
}

.upload-area:hover {
    border-color: #8b5cf6;
    background: rgba(240, 247, 255, 0.8);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
}

.upload-area.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    border-color: #e5e7eb;
    background: #f9fafb;
    transform: none;
    box-shadow: none;
}

.upload-icon {
    margin-bottom: 8px;
    color: #8b5cf6;
    transition: all 0.3s ease;
}

.upload-text {
    color: #8b5cf6;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
}

.upload-hint {
    font-size: 9px;
    color: #9ca3af;
    text-align: center;
    line-height: 1.1;
}

.media-preview {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid rgba(229, 231, 235, 0.8);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.media-preview:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.media-preview img,
.media-preview video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.media-number {
    position: absolute;
    top: 6px;
    left: 6px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 12px;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 8px;
    backdrop-filter: blur(5px);
}

.remove-media {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    background: rgba(239, 68, 68, 0.9);
    color: white;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.remove-media:hover {
    background: #ef4444;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.media-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #f59e0b;
    font-size: 14px;
    font-weight: 500;
    margin-top: 16px;
    padding: 10px 15px;
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid #fcd34d;
    border-radius: 10px;
    backdrop-filter: blur(5px);
}

.modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(229, 231, 235, 0.8);
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-primary {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.4);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.btn-secondary {
    background: rgba(255, 255, 255, 0.9);
    color: #8b5cf6;
    border: 2px solid rgba(139, 92, 246, 0.3);
    backdrop-filter: blur(10px);
}

.btn-secondary:hover {
    background: rgba(139, 92, 246, 0.1);
    border-color: #8b5cf6;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.2);
}

@media (max-width: 768px) {
    .modal-content {
        width: 95%;
        margin: 20px;
    }

    .modal-body {
        padding: 24px;
    }

    .modal-header {
        padding: 20px 24px;
    }

    .modal-title {
        font-size: 20px;
    }

    .type-options {
        flex-direction: column;
    }

    .media-type-tabs {
        flex-direction: column;
    }

    .modal-actions {
        flex-direction: column;
        gap: 12px;
    }
}

::v-deep .el-dialog {
    border-radius: 30px;
    z-index: 10000 !important;
}

::v-deep .el-dialog__header {
    padding: 0;
    z-index: 1 !important;
}

::v-deep .el-dialog__body {
    padding: 24px;
}

::v-deep .el-dialog {
    margin-top: 5vh !important;

    .el-dialog__body {
        height: calc(90vh - 10px);
        box-sizing: border-box;
    }
}

/* 确保attachment组件的dialog在最顶层 */
::v-deep .attachment .el-dialog {
    z-index: 10000 !important;
}

::v-deep .attachment .el-dialog__wrapper {
    z-index: 10000 !important;
}

/* 字符计数警告样式 */
.char-warning {
    color: #f56565;
    font-weight: 600;
}

.min-char-hint {
    color: #f56565;
    font-size: 12px;
    margin-left: 8px;
    font-style: italic;
}

/* 提示信息样式 */
.greeting-hint {
    background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
    border-left: 4px solid #f56565;
    border-radius: 8px;
    padding: 12px 16px;
    margin: 12px 0 16px 0;
    color: #c53030;
    font-size: 14px;
    line-height: 1.6;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    box-shadow: 0 2px 8px rgba(245, 101, 101, 0.1);
    transition: all 0.3s ease;
}

.greeting-hint .hint-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.greeting-hint .hint-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.greeting-hint:hover {
    box-shadow: 0 4px 12px rgba(245, 101, 101, 0.15);
    transform: translateY(-1px);
}

.greeting-hint i {
    color: #f56565;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
}

.greeting-hint .hint-label {
    color: #e53e3e;
    font-weight: 600;
    margin-right: 4px;
}

.greeting-hint strong {
    color: #c53030;
    font-weight: 700;
    background: rgba(245, 101, 101, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
}

.greeting-hint .example-text {
    color: #e53e3e;
    background: rgba(229, 62, 62, 0.15);
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 700;
    margin-left: 4px;
}

::v-deep .create-content-confirm .el-message-box {
    max-width: 600px;
    max-height: 500px;
    overflow: hidden;
}

::v-deep .create-content-confirm .el-message-box__container {
    max-height: 360px;
    overflow-y: auto;
}

::v-deep .create-content-confirm .el-message-box__content {
    padding-top: 0;
    padding-right: 6px;
}

::v-deep .create-content-confirm-body {
    line-height: 1.6;
    color: #374151;
}

::v-deep .create-content-confirm-body p {
    margin: 6px 0;
}

::v-deep .create-content-confirm-body strong {
    color: #111827;
}
</style>
